import * as http from "./http";
import {CDUploadProgress, CDUploadStatus} from "../types/CDUpload";
import {DicomizeProgress, DicomizeStatus} from "../types/Dicomize";
import {ErrorCode} from "../types/Error";

type PollingProgress = CDUploadProgress | DicomizeProgress;
type PollingStatus = CDUploadStatus | DicomizeStatus;

type Handlers<TProgress extends PollingProgress> = {
	onProgress: (p: TProgress) => void;
	onSuccess: (p: TProgress) => void;
	onError: (e: ErrorCode) => void;
};

const isTerminalStatus = (status: PollingStatus): boolean => {
	return status == "ABORTED" || status === "SUCCESS" || status === "FAILED" || status === "IDLE";
}

export class ProgressPoller<TProgress extends PollingProgress> {
	private abortController: AbortController | null = null;
	private stopped = false;

	async start(uuid: string, handlers: Handlers<TProgress>): Promise<void> {
		this.stop();
		this.stopped = false;

		while (!this.stopped) {
			try {
				this.abortController = new AbortController();

				const path = `/web/progress?uuid=${encodeURIComponent(uuid)}`;
				const res = await http.get<PollingProgress>(path, this.abortController.signal);

				handlers.onProgress(res as TProgress);

				if (isTerminalStatus(res.status)) {
					if (res.status === "FAILED") {
						handlers.onError(ErrorCode.SERVICE_FAILED);
					} else {
						handlers.onSuccess(res as TProgress);
					}
					this.stop();
					break;
				}
			} catch (err) {
				handlers.onError(ErrorCode.SERVICE_PROGRESS_FAILED);
				this.stop();
			} finally {
				this.abortController = null;
			}

			await new Promise((r) => setTimeout(r, 1000));
		}
	}

	stop() {
		this.stopped = true;
		if (this.abortController) {
			this.abortController.abort();
			this.abortController = null;
		}
	}
}